import dayjs from 'dayjs'

/**
 * Date buckets used to break the flat notification list into readable
 * sections on the Notifications screen.
 */
export type NotificationDateGroup = 'today' | 'thisWeek' | 'earlier'

export interface NotificationSection<T> {
  key: NotificationDateGroup
  data: T[]
}

// Ordered so the most recent bucket renders first.
const GROUP_ORDER: ReadonlyArray<NotificationDateGroup> = ['today', 'thisWeek', 'earlier']

const resolveGroup = (createdAt: string, now: dayjs.Dayjs): NotificationDateGroup => {
  const date = dayjs(createdAt)
  if (date.isSame(now, 'day')) return 'today'
  if (date.isAfter(now.startOf('week'))) return 'thisWeek'
  return 'earlier'
}

/**
 * Groups notifications by how recent they are (today / this week / earlier).
 *
 * Input order is preserved inside each bucket — the API already returns the
 * newest notifications first, so callers get a stable, newest-first list per
 * section. Empty buckets are dropped so the UI never renders a header with no
 * rows under it.
 */
export function groupNotificationsByDate<T extends { createdAt: string }>(
  items: T[],
): NotificationSection<T>[] {
  const now = dayjs()
  const buckets: Record<NotificationDateGroup, T[]> = {
    today: [],
    thisWeek: [],
    earlier: [],
  }

  items.forEach((item) => {
    buckets[resolveGroup(item.createdAt, now)].push(item)
  })

  return GROUP_ORDER.filter((key) => buckets[key].length > 0).map((key) => ({
    key,
    data: buckets[key],
  }))
}
